import { createSchema } from 'react-hook-form-auto'
import gql from 'graphql-tag'

import { createDeleteUpdater, fileFragment } from '../pintor'
import { useMutation } from '../graphql/notificatingMutation'
import { useSubscription } from "@apollo/client/react";

export const NutrimentSchema = createSchema('nutriment', {
  type: {
    type: 'select',
    options: [
      'sugars',
      'salt',
      'proteins',
      'saturatedFat',
      'sodium',
      'energy',
      'fat',
      'carbohidrates'
    ]
  },
  amount: {
    type: 'number'
  },
  unit: {
    type: 'select',
    options: [
      'kcal',
      'kJ',
      'kg',
      'g',
      'ml',
      'l',
      'percentVol'
    ]
  }
})

export const IngredientSchema = createSchema('ingredient', {
  name: {
    type: 'string',
    required: true,
    max: 255
  },
  groupId: {
    type: 'select',
    coerce: value => value === '' ? null : parseInt(value),
    options: props => {
      const groups = props && props.groups || []

      return groups.map(group => ({
        value: group.id,
        label: group.name
      }))
    },
    associatedData: 'groups',
    labelField: 'name'
  },
  placeIds: {
    type: 'multiselect',
    associatedData: 'places',
    labelField: 'name'
  },
  skipAutoBuy: {
    type: 'boolean',
    defaultValue: false
  },
  nutriments: {
    type: [NutrimentSchema],
    initiallyEmpty: true
  },
  imageIds: {
    type: 'images',
    onlyOne: true
  }
})

export const ingredientFragment = gql`
  fragment IngredientFragment on Ingredient {
    id
    createdAt
    name
    skipAutoBuy
    nutriments {
      type
      amount
      unit
    }
    images {
      ...FileFragment
    }
    group {
      id
      name
    }
    places {
      id
      name
    }
  }

  ${fileFragment}
`

const INGREDIENTS_GET = gql`
  query IngredientsList(
    $search: String,
    $first: Int,
    $num: Int,
    $seeEveryones: Boolean
  ) {
    collection: ingredients(
      search: $search,
      first: $first,
      num: $num,
      seeEveryones: $seeEveryones
    ) {
      ...IngredientFragment
    }
  }
  ${ingredientFragment}
`

const INGREDIENTS_SUB = gql`
  subscription IngredientUpdate {
    ingredientChange {
      ...IngredientFragment
    }
  }
  ${ingredientFragment}
`

const INGREDIENTS_COUNT = gql`
  query IngredientCount($search: String, $seeEveryones: Boolean) {
    count: ingredientsCount(search: $search, seeEveryones: $seeEveryones)
  }
`

const INGREDIENT_GET = gql`
  query IngredientGet($id: ID!) {
    ingredient(id: $id) {
      ...IngredientFragment
    }
  }
  ${ingredientFragment}
`

const INGREDIENT_UPDATE = gql`
  mutation IngredientUpdate($input: IngredientInput!) {
    ingredientUpdate(input: $input) {
      ...IngredientFragment
    }
  }
  ${ingredientFragment}
`

const INGREDIENT_DELETE = gql`
  mutation IngredientDelete($id: ID!) {
    ingredientDelete(id: $id)
  }
`

const INGREDIENT_DELETED_SUB = gql`
  subscription IngredientDeletedSub {
    ingredientDeleted
  }
`

function formConvert(doc) {
  const images = doc.images || []
  const places = doc.places || []

  return {
    ...doc,
    imageIds: images.map(image => image.id),
    groupId: doc.group && doc.group.id,
    placeIds: places.map(place => String(place.id))
  }
}

function mutationConvert(doc) {
  let {
    __typename,
    images,
    imageIds,
    nutriments,
    group,
    groupId,
    places,
    placeIds,
    createdAt,
    ...rest
  } = doc

  const useIds = imageIds || images.map(image => image.id)
  const usePlaceIds = placeIds ||
    (places || []).map(place => String(place.id))

  return {
    variables: {
      input: {
        ...rest,
        imageIds: useIds,
        placeIds: usePlaceIds,
        groupId: groupId || (group && group.id),
        nutriments: nutriments.map(({ type, amount, unit }) => ({
          type,
          amount,
          unit
        }))
      }
    }
  }
}

function useUpdate(options = {}) {
  const [ mutate ] = useMutation(INGREDIENT_UPDATE, options)

  return async doc => {
    const converted = mutationConvert(doc)
    const result = await mutate(converted)

    return result && result.data && result.data.ingredientUpdate
  }
}

function useIngredientDelete(options = {}) {
  return useMutation(INGREDIENT_DELETE, options)
}

const ingredientDeleter = createDeleteUpdater({
  query: INGREDIENTS_GET,
  subscriptionName: 'ingredientDeleted',
  collectionKey: 'ingredients',
  typename: 'Ingredient'
})
function useIngredientDeleted() {
  const { data, loading } = useSubscription(INGREDIENT_DELETED_SUB, {
    onData: ingredientDeleter
  })
  const ingredientDeleted = data && data.ingredientDeleted

  return [ ingredientDeleted, loading ]
}

export const Ingredient = {
  name: 'ingredient',
  schema: IngredientSchema,
  search: INGREDIENTS_GET,
  fetch: INGREDIENTS_GET,
  sub: INGREDIENTS_SUB,
  mainSubName: 'ingredientChange',
  mainSubCollection: 'collection',
  mainStoreKey: 'ingredients',
  count: INGREDIENTS_COUNT,
  get: INGREDIENT_GET,
  update: INGREDIENT_UPDATE,
  delete: INGREDIENT_DELETE,
  useUpdate,
  useIngredientDelete,
  useIngredientDeleted,
  fragment: ingredientFragment,
  formConvert,
  mutationConvert
}
